import React, { useState, useEffect } from 'react';
import TipTapEditor from './TipTapEditor';
import ImageUpload from './ImageUpload';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { Plus, Trash2, Image, MoveUp, MoveDown, Grid, Sparkles, Link, Type, FileUp, RefreshCw } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { adminApi } from '../services/adminApi';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RichBlogEditor = ({ initialData, onSave, onCancel }) => {
  const { toast } = useToast();
  const [mainTitle, setMainTitle] = useState(initialData?.title || '');
  const [mainTitleEn, setMainTitleEn] = useState(initialData?.title_en || '');
  const [excerpt, setExcerpt] = useState(initialData?.excerpt || '');
  const [excerptEn, setExcerptEn] = useState(initialData?.excerpt_en || '');
  const [category, setCategory] = useState(initialData?.category || 'Real Estate');
  const [categoryEn, setCategoryEn] = useState(initialData?.category_en || 'Real Estate');
  const [slug, setSlug] = useState(initialData?.slug || '');
  const [featuredImage, setFeaturedImage] = useState(initialData?.image || '');
  const [published, setPublished] = useState(initialData?.published !== undefined ? initialData.published : true);
  const [unlisted, setUnlisted] = useState(initialData?.unlisted || false);
  const [sections, setSections] = useState(initialData?.sections || []);
  const [uploading, setUploading] = useState(false);
  
  // AI Generation states
  const [showAIGenerate, setShowAIGenerate] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [aiSource, setAiSource] = useState('url');
  const [aiUrl, setAiUrl] = useState('');
  const [aiText, setAiText] = useState('');
  const [aiInstructions, setAiInstructions] = useState('');
  const [aiPdfFile, setAiPdfFile] = useState(null);

  const getToken = () => localStorage.getItem('admin_token');

  const generateBlogWithAI = async () => {
    setGenerating(true);
    try {
      let response;
      
      if (aiSource === 'url') {
        if (!aiUrl) {
          toast({ title: 'Erro', description: 'Insira uma URL', variant: 'destructive' });
          setGenerating(false);
          return;
        }
        response = await axios.post(`${API_URL}/api/admin/ai/generate/from-url`, {
          url: aiUrl,
          content_type: 'blog',
          language: 'pt',
          additional_instructions: aiInstructions
        }, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
      } else if (aiSource === 'text') {
        if (!aiText) {
          toast({ title: 'Erro', description: 'Insira o texto', variant: 'destructive' });
          setGenerating(false);
          return;
        }
        response = await axios.post(`${API_URL}/api/admin/ai/generate/from-text`, {
          text: aiText,
          content_type: 'blog',
          language: 'pt',
          additional_instructions: aiInstructions
        }, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
      } else if (aiSource === 'pdf') {
        if (!aiPdfFile) {
          toast({ title: 'Erro', description: 'Selecione um PDF', variant: 'destructive' });
          setGenerating(false);
          return;
        }
        const formData = new FormData();
        formData.append('file', aiPdfFile);
        formData.append('content_type', 'blog');
        formData.append('language', 'pt');
        formData.append('additional_instructions', aiInstructions);
        
        response = await axios.post(`${API_URL}/api/admin/ai/generate/from-pdf`, formData, {
          headers: { 
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      
      if (response?.data?.success && response.data.generated) {
        const gen = response.data.generated;
        
        // Fill in the form with generated content
        setMainTitle(gen.title || '');
        setMainTitleEn(gen.title_en || '');
        setExcerpt(gen.excerpt || '');
        setExcerptEn(gen.excerpt_en || '');
        setCategory(gen.category || 'Mercado Imobiliário');
        setCategoryEn(gen.category_en || 'Real Estate Market');
        
        // Create a section with the content
        if (gen.content) {
          const newSection = {
            id: `section-ai-${Date.now()}`,
            order: 0,
            type: 'text',
            title: '',
            title_en: '',
            content: gen.content,
            content_en: gen.content_en || '',
            images: [],
            imagePosition: 'left'
          };
          setSections([newSection]);
        }
        
        setShowAIGenerate(false);
        toast({ title: 'Sucesso', description: 'Blog gerado com IA! Revise e ajuste antes de salvar.' });
        
        // Reset AI form
        setAiUrl('');
        setAiText('');
        setAiInstructions('');
        setAiPdfFile(null);
      }
    } catch (error) {
      toast({
        title: 'Erro',
        description: error.response?.data?.detail || 'Erro ao gerar conteúdo',
        variant: 'destructive'
      });
    } finally {
      setGenerating(false);
    }
  };

  // Contar total de imagens
  const totalImages = sections.reduce((count, section) => {
    return count + (section.images?.length || 0);
  }, 0) + (featuredImage ? 1 : 0);

  const addSection = (type) => {
    if (type === 'gallery' && totalImages >= 15) {
      toast({
        title: 'Limite de imagens',
        description: 'Máximo de 15 imagens por post',
        variant: 'destructive'
      });
      return;
    }

    const newSection = {
      id: `section-${Date.now()}`,
      order: sections.length,
      type: type, // "text", "text_with_image", "gallery"
      title: '',
      title_en: '',
      content: '',
      content_en: '',
      images: [],
      imagePosition: 'left'
    };
    setSections([...sections, newSection]);
  };

  const removeSection = (index) => {
    const newSections = sections.filter((_, i) => i !== index);
    // Reordenar
    newSections.forEach((section, idx) => {
      section.order = idx;
    });
    setSections(newSections);
  };

  const moveSection = (index, direction) => {
    const newSections = [...sections];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newSections.length) return;
    
    [newSections[index], newSections[targetIndex]] = [newSections[targetIndex], newSections[index]];
    
    // Atualizar ordem
    newSections.forEach((section, idx) => {
      section.order = idx;
    });
    
    setSections(newSections);
  };

  const updateSection = (index, field, value) => {
    const newSections = [...sections];
    newSections[index][field] = value;
    setSections(newSections);
  };

  // Comprimir imagem antes do upload (converte HEIC/qualquer formato para JPEG)
  const compressImage = (file, maxWidth = 1920, quality = 0.85) => {
    return new Promise((resolve) => {
      // Se o arquivo for pequeno (<1MB), não comprimir
      if (file.size < 1024 * 1024) {
        resolve(file);
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob(
            (blob) => {
              const compressed = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
                type: 'image/jpeg',
              });
              resolve(compressed);
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = () => resolve(file); // fallback
        img.src = e.target.result;
      };
      reader.onerror = () => resolve(file); // fallback
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (e, index) => {
    const files = Array.from(e.target.files);
    const section = sections[index];
    
    // Verificar limite
    const currentImages = section.images?.length || 0;
    const newImagesCount = files.length;
    
    if (totalImages + newImagesCount > 15) {
      toast({
        title: 'Limite de imagens',
        description: `Você pode adicionar no máximo ${15 - totalImages} imagens`,
        variant: 'destructive'
      });
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      // Upload cada imagem individualmente para garantir URLs completas
      const uploadedUrls = [];
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      for (const file of files) {
        const compressed = await compressImage(file);
        const formData = new FormData();
        formData.append('file', compressed);
        
        const response = await adminApi.uploadImage(token, formData);
        
        if (response.success) {
          // Usar rota da API para servir imagens (funciona em produção)
          const filename = response.filename;
          const fullUrl = `${BACKEND_URL}/api/admin/image/${filename}`;
          uploadedUrls.push(fullUrl);
        }
      }
      
      const newSections = [...sections];
      newSections[index].images = [...(newSections[index].images || []), ...uploadedUrls];
      setSections(newSections);
      
      toast({
        title: 'Sucesso',
        description: `${uploadedUrls.length} imagem(ns) enviada(s)`
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao fazer upload das imagens',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (sectionIndex, imageIndex) => {
    const newSections = [...sections];
    newSections[sectionIndex].images = newSections[sectionIndex].images.filter((_, i) => i !== imageIndex);
    setSections(newSections);
  };

  const handleSave = () => {
    // Validação
    console.log('Validating fields:', {
      mainTitle,
      excerpt,
      category,
      featuredImage
    });
    
    // Validação dos campos obrigatórios
    if (!mainTitle || !mainTitle.trim()) {
      toast({
        title: 'Campo obrigatório',
        description: 'Preencha o título principal em português',
        variant: 'destructive'
      });
      return;
    }
    
    if (!excerpt || !excerpt.trim()) {
      toast({
        title: 'Campo obrigatório',
        description: 'Preencha o resumo em português',
        variant: 'destructive'
      });
      return;
    }
    
    if (!category || !category.trim()) {
      toast({
        title: 'Campo obrigatório',
        description: 'Preencha a categoria',
        variant: 'destructive'
      });
      return;
    }
    
    if (!featuredImage || !featuredImage.trim()) {
      toast({
        title: 'Imagem obrigatória',
        description: 'Faça upload da imagem principal (Featured)',
        variant: 'destructive'
      });
      return;
    }

    const postData = {
      id: initialData?.id || `post-${Date.now()}`,
      slug: slug || undefined, // Slug (auto-gerado no backend se vazio)
      title: mainTitle,
      title_en: mainTitleEn,
      excerpt: excerpt,
      excerpt_en: excerptEn,
      content: excerpt, // Manter compatibilidade com posts antigos
      content_en: excerptEn,
      category: category,
      category_en: categoryEn,
      image: featuredImage,
      sections: sections,
      published: published,
      unlisted: unlisted,
      date: initialData?.date || new Date().toISOString()
    };

    onSave(postData);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Gerar com IA - NO TOPO */}
      <Card className="border-2 border-purple-200 bg-purple-50">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-purple-900 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Gerar Blog com IA
              </h3>
              <p className="text-sm text-purple-700">Crie um blog a partir de um link, texto ou PDF</p>
            </div>
            <Button 
              type="button"
              onClick={() => setShowAIGenerate(true)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Gerar com IA
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Status de Publicação */}
      <Card className="border-2 border-blue-200 bg-blue-50">
        <CardContent className="p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold mb-4">Status de Publicação</h3>
          <div className="flex flex-wrap gap-6">
            {/* Publicado */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="published"
                checked={published}
                onChange={(e) => setPublished(e.target.checked)}
                className="w-5 h-5 accent-green-600"
              />
              <label htmlFor="published" className="text-sm font-medium">
                Publicado
              </label>
            </div>

            {/* Não Listado */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="unlisted"
                checked={unlisted}
                onChange={(e) => setUnlisted(e.target.checked)}
                disabled={!published}
                className="w-5 h-5 accent-amber-600"
              />
              <label htmlFor="unlisted" className={`text-sm font-medium ${!published ? 'text-gray-400' : ''}`}>
                Não Listado (visível apenas com link direto)
              </label>
            </div>
          </div>
          
          {/* Status atual */}
          <div className="mt-3 flex gap-2">
            {!published && (
              <span className="text-xs px-2 py-1 rounded bg-gray-200 text-gray-700">
                Rascunho - não visível no site
              </span>
            )}
            {published && !unlisted && (
              <span className="text-xs px-2 py-1 rounded bg-green-200 text-green-700">
                Publicado - visível no blog
              </span>
            )}
            {published && unlisted && (
              <span className="text-xs px-2 py-1 rounded bg-amber-200 text-amber-700">
                Não Listado - visível apenas com link direto
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Contador de Imagens */}
      <div className="bg-brand-light p-3 sm:p-4 rounded-lg border border-brand-medium-light200 space-y-1">
        <p className="text-xs sm:text-sm font-medium">
          Imagens usadas: <span className={totalImages > 15 ? 'text-red-600' : 'text-brand-dark'}>{totalImages}/15</span>
        </p>
        {featuredImage && (
          <p className="text-xs text-green-600">
            ✓ Imagem principal carregada
          </p>
        )}
      </div>

      {/* Campos principais */}
      <Card>
        <CardContent className="p-4 sm:p-6 space-y-3 sm:space-y-4">
          <h3 className="text-base sm:text-lg font-semibold">Informações Principais</h3>
          
          {/* Português */}
          <div className="border-l-4 border-brand-medium-light500 pl-4 space-y-3">
            <h4 className="font-medium flex items-center gap-2">
              <span className="text-2xl">🇵🇹</span> Português
            </h4>
            <div>
              <label className="text-sm font-medium">Título Principal (PT)*</label>
              <Input 
                value={mainTitle} 
                onChange={(e) => setMainTitle(e.target.value)} 
                placeholder="Ex: Investir em Lisboa"
                required 
              />
            </div>
            <div>
              <label className="text-sm font-medium">Resumo (PT)*</label>
              <Textarea 
                value={excerpt} 
                onChange={(e) => setExcerpt(e.target.value)} 
                placeholder="Breve resumo do post"
                required
                rows={3}
                className="resize-y"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Categoria (PT)*</label>
              <Input 
                value={category} 
                onChange={(e) => setCategory(e.target.value)} 
                placeholder="Ex: Real Estate"
                required 
              />
            </div>
          </div>

          {/* Inglês */}
          <div className="border-l-4 border-red-500 pl-4 space-y-3">
            <h4 className="font-medium flex items-center gap-2">
              <span className="text-2xl">🇬🇧</span> English
            </h4>
            <div>
              <label className="text-sm font-medium">Main Title (EN)</label>
              <Input value={mainTitleEn} onChange={(e) => setMainTitleEn(e.target.value)} placeholder="English translation" />
            </div>
            <div>
              <label className="text-sm font-medium">Excerpt (EN)</label>
              <Textarea 
                value={excerptEn} 
                onChange={(e) => setExcerptEn(e.target.value)} 
                placeholder="English translation"
                rows={3}
                className="resize-y"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Category (EN)</label>
              <Input value={categoryEn} onChange={(e) => setCategoryEn(e.target.value)} placeholder="English translation" />
            </div>
          </div>

          {/* SEO Slug */}
          <div className="border-l-4 border-blue-500 pl-4 space-y-3">
            <h4 className="font-medium flex items-center gap-2">
              <span className="text-2xl">🔗</span> SEO Slug
            </h4>
            <div>
              <label className="text-sm font-medium">Slug (URL amigável)</label>
              <Input 
                value={slug} 
                onChange={(e) => setSlug(e.target.value)} 
                placeholder="exemplo: guia-investir-portugal-2025"
                className="font-mono text-sm"
              />
              <p className="text-xs text-slate-500 mt-1">
                Preview: yourbrokerportugal.com/blog/{slug || 'slug-automatico'}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                💡 Deixe vazio para gerar automaticamente a partir do título
              </p>
            </div>
          </div>

          {/* Imagem principal */}
          <ImageUpload
            label="Imagem Principal (Featured)*"
            value={featuredImage}
            onChange={setFeaturedImage}
          />
        </CardContent>
      </Card>

      {/* Seções */}
      <div className="space-y-3 sm:space-y-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <h3 className="text-base sm:text-lg font-semibold">Seções do Post</h3>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <Button type="button" size="sm" onClick={() => addSection('text')} variant="outline" className="flex-1 sm:flex-none text-xs">
              <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              Texto
            </Button>
            <Button type="button" size="sm" onClick={() => addSection('text_with_image')} variant="outline" className="flex-1 sm:flex-none text-xs">
              <Image className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              Texto + Img
            </Button>
            <Button type="button" size="sm" onClick={() => addSection('gallery')} variant="outline" className="flex-1 sm:flex-none text-xs">
              <Grid className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              Galeria
            </Button>
          </div>
        </div>

        {sections.length === 0 && (
          <Card>
            <CardContent className="p-6 sm:p-8 text-center text-gray-500">
              <p className="text-sm">Nenhuma seção adicionada ainda. Clique nos botões acima para começar.</p>
            </CardContent>
          </Card>
        )}

        {sections.map((section, index) => (
          <Card key={section.id}>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-brand-light text-brand-dark text-xs rounded font-medium">
                    {section.type === 'text' ? 'Texto' : section.type === 'text_with_image' ? 'Texto + Imagem' : 'Galeria'}
                  </span>
                  <span className="text-sm text-gray-500">Seção {index + 1}</span>
                </div>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="ghost" onClick={() => moveSection(index, 'up')} disabled={index === 0}>
                    <MoveUp className="w-4 h-4" />
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => moveSection(index, 'down')} disabled={index === sections.length - 1}>
                    <MoveDown className="w-4 h-4" />
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => removeSection(index)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>

              {/* Título da Seção PT */}
              <div className="border-l-4 border-brand-medium-light500 pl-4 space-y-3">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <span className="text-xl">🇵🇹</span> Português
                </h4>
                <div>
                  <label className="text-sm font-medium">Título da Seção (PT)</label>
                  <Input 
                    value={section.title} 
                    onChange={(e) => updateSection(index, 'title', e.target.value)}
                    placeholder="Opcional"
                  />
                </div>
                {section.type !== 'gallery' && (
                  <div>
                    <label className="text-sm font-medium">Conteúdo (PT)</label>
                    <TipTapEditor
                      content={section.content}
                      onChange={(value) => updateSection(index, 'content', value)}
                      placeholder="Escreva o conteúdo em português..."
                    />
                  </div>
                )}
              </div>

              {/* Título da Seção EN */}
              <div className="border-l-4 border-red-500 pl-4 space-y-3">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <span className="text-xl">🇬🇧</span> English
                </h4>
                <div>
                  <label className="text-sm font-medium">Section Title (EN)</label>
                  <Input 
                    value={section.title_en} 
                    onChange={(e) => updateSection(index, 'title_en', e.target.value)}
                    placeholder="Optional"
                  />
                </div>
                {section.type !== 'gallery' && (
                  <div>
                    <label className="text-sm font-medium">Content (EN)</label>
                    <TipTapEditor
                      content={section.content_en}
                      onChange={(value) => updateSection(index, 'content_en', value)}
                      placeholder="Write content in english..."
                    />
                  </div>
                )}
              </div>

              {/* Imagens */}
              {section.type !== 'text' && (
                <div>
                  <label className="text-sm font-medium">Imagens ({section.images?.length || 0})</label>
                  
                  {section.type === 'text_with_image' && (
                    <div className="mb-2">
                      <label className="text-sm font-medium">Posição da Imagem</label>
                      <select 
                        className="w-full border rounded p-2 text-sm"
                        value={section.imagePosition}
                        onChange={(e) => updateSection(index, 'imagePosition', e.target.value)}
                      >
                        <option value="left">Esquerda</option>
                        <option value="right">Direita</option>
                        <option value="full">Largura Total</option>
                      </select>
                    </div>
                  )}
                  
                  <input
                    type="file"
                    multiple={section.type === 'gallery'}
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, index)}
                    className="w-full border rounded p-2 text-sm"
                    disabled={uploading || totalImages >= 15}
                  />
                  
                  {section.images && section.images.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {section.images.map((img, imgIdx) => (
                        <div key={imgIdx} className="relative">
                          <img src={img} alt={`Section ${index + 1} Image ${imgIdx + 1}`} className="w-full h-24 object-cover rounded" />
                          <button
                            type="button"
                            onClick={() => removeImage(index, imgIdx)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Botões de Ação */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 sticky bottom-0 bg-white p-4 -mx-4 sm:-mx-0 border-t sm:border-0 shadow-lg sm:shadow-none">
        <Button type="button" onClick={handleSave} className="flex-1 w-full">
          Salvar Post
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1 w-full">
          Cancelar
        </Button>
      </div>

      {/* AI Generate Dialog */}
      <Dialog open={showAIGenerate} onOpenChange={setShowAIGenerate}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Gerar Blog com IA
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Source Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">Fonte do conteúdo</label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={aiSource === 'url' ? 'default' : 'outline'}
                  onClick={() => setAiSource('url')}
                  size="sm"
                >
                  <Link className="w-4 h-4 mr-2" />
                  URL / Link
                </Button>
                <Button
                  type="button"
                  variant={aiSource === 'text' ? 'default' : 'outline'}
                  onClick={() => setAiSource('text')}
                  size="sm"
                >
                  <Type className="w-4 h-4 mr-2" />
                  Texto
                </Button>
                <Button
                  type="button"
                  variant={aiSource === 'pdf' ? 'default' : 'outline'}
                  onClick={() => setAiSource('pdf')}
                  size="sm"
                >
                  <FileUp className="w-4 h-4 mr-2" />
                  PDF
                </Button>
              </div>
            </div>

            {/* URL Input */}
            {aiSource === 'url' && (
              <div>
                <label className="block text-sm font-medium mb-2">URL do conteúdo</label>
                <Input
                  value={aiUrl}
                  onChange={(e) => setAiUrl(e.target.value)}
                  placeholder="https://exemplo.com/pagina-imovel"
                />
                <p className="text-xs text-gray-500 mt-1">Cole o link de um imóvel, empreendimento ou notícia</p>
              </div>
            )}

            {/* Text Input */}
            {aiSource === 'text' && (
              <div>
                <label className="block text-sm font-medium mb-2">Texto de referência</label>
                <Textarea
                  value={aiText}
                  onChange={(e) => setAiText(e.target.value)}
                  placeholder="Cole aqui informações sobre o imóvel, região, empreendimento..."
                  rows={6}
                />
              </div>
            )}

            {/* PDF Input */}
            {aiSource === 'pdf' && (
              <div>
                <label className="block text-sm font-medium mb-2">Ficheiro PDF</label>
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setAiPdfFile(e.target.files?.[0] || null)}
                />
                <p className="text-xs text-gray-500 mt-1">Upload de brochura, ficha técnica ou documento</p>
              </div>
            )}

            {/* Additional Instructions */}
            <div>
              <label className="block text-sm font-medium mb-2">Instruções adicionais (opcional)</label>
              <Textarea
                value={aiInstructions}
                onChange={(e) => setAiInstructions(e.target.value)}
                placeholder="Ex: Foque em investidores, mencione Golden Visa, destaque a localização..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAIGenerate(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={generateBlogWithAI}
              disabled={generating}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {generating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Gerando...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Gerar Blog
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RichBlogEditor;
